import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";

interface PacemakerModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (value: string) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const PacemakerModal: React.FC<PacemakerModalProps> = ({
  open,
  onClose,
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const [pacemakerInfo, setPacemakerInfo] = useState<string>("");
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (open) {
      setPacemakerInfo("");
    }
  }, [open]);

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await referenceService.additionalInfo.getFields({
          formtype: "PACEMAKER_CONTROL",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        if (res) {
          setAdditionalInfo(res);
        }


        const field = res.fields && res.fields.length > 0 ? res.fields[0] : null;
        if (field && Array.isArray(field.options)) {
          const mapped = field.options.map((opt) => ({
            value: String(opt.value),
            label: opt.label ?? "",
          }));
          setOptions(mapped);
        } else {
          // Fallback: sabit liste
          setOptions([
            { value: "0", label: "Hayır yok" },
            { value: "1", label: "Evet var" },
          ]);
        }
      } catch {
        // Hata durumunda mevcut manuel akış devam eder; sabit listeye düş
        setOptions([
          { value: "0", label: "Hayır yok" },
          { value: "1", label: "Evet var" },
        ]);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    if (!pacemakerInfo) return;
    onSave?.(pacemakerInfo);
    if (!onSave) {
      onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.pacemaker.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!pacemakerInfo} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pacemaker.info")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.pacemaker.info")}
              value={
                options.find((opt) => opt.value === pacemakerInfo)?.label || ""
              }
              selectedId={pacemakerInfo || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setPacemakerInfo(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default PacemakerModal;
